import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-upload-how',
    category: 'data',
    title: 'Μεταφόρτωση αρχείου, βήμα προς βήμα',
    summary: 'Πώς να μεταφορτώσετε ένα CSV και να διαβάσετε το αποτέλεσμα επαλήθευσης.',
    body: (
      <>
        <p>Μόλις είναι έτοιμο το αρχείο CSV, μεταφορτώστε το από μέσα στην κοινότητα:</p>
        <ol>
          <li>Ανοίξτε την κοινότητά σας και επιλέξτε <strong>Διαχείριση Δεδομένων</strong> στο αριστερό μενού.</li>
          <li>Στο <strong>Επιλογή τύπου συνόλου δεδομένων</strong>, επιλέξτε τον τύπο του αρχείου που μεταφορτώνετε
            (για παράδειγμα «Building Load Profile»).</li>
          <li>Αν ζητηθεί, επιλέξτε σε ποιο <strong>κτίριο</strong> ανήκει το αρχείο.</li>
          <li>Σύρετε το αρχείο CSV στην περιοχή μεταφόρτωσης ή κάντε κλικ στο <strong>Αναζήτηση αρχείων</strong>.</li>
          <li>Κάντε κλικ στο <strong>Επαλήθευση &amp; Προεπισκόπηση</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="upload-page.png"
          description="The community Data page with a dataset type selected, showing the required-columns badges, the download-template button, and the drag-and-drop upload area."
          source="Platform — open any community → click 'Data Management' in the sidebar → pick a dataset type from the dropdown so the upload area appears. Capture the form area."
          caption="Η σελίδα Δεδομένων: επιλέξτε τύπο και αποθέστε το αρχείο σας."
        />
        <h5 className="mt-4">Ανάγνωση του αποτελέσματος</h5>
        <ul>
          <li><strong>Πράσινο banner</strong> — το αρχείο είναι έγκυρο. Εμφανίζεται ο αριθμός των γραμμών που διαβάστηκαν και το εύρος ημερομηνιών. Έχετε τελειώσει.</li>
          <li><strong>Κίτρινες προειδοποιήσεις</strong> — το αρχείο έγινε αποδεκτό αλλά κάτι αξίζει να ελεγχθεί (για παράδειγμα, λείπουν ώρες). Μπορείτε να το κρατήσετε ή να βελτιώσετε το αρχείο.</li>
          <li><strong>Κόκκινο banner</strong> — το αρχείο απορρίφθηκε. Η προεπισκόπηση παραθέτει κάθε πρόβλημα με τον αριθμό γραμμής του. Διορθώστε το αρχείο και μεταφορτώστε το ξανά.</li>
        </ul>
        <ScreenshotPlaceholder
          img="upload-validation-result.png"
          description="The validation result panel after a successful upload — green success banner plus the data preview table."
          source="Platform — on the same Data Management page, after uploading a valid CSV and clicking 'Validate & Preview'. Capture the green banner and the first few rows of the preview table."
          caption="Μια επιτυχής μεταφόρτωση εμφανίζει πράσινο banner και προεπισκόπηση."
        />
        <p>
          Κάθε κόκκινο μήνυμα σφάλματος εξηγείται στο
          <em> «Γιατί απέτυχε η μεταφόρτωσή μου;»</em> στην ενότητα Αντιμετώπιση προβλημάτων.
        </p>
      </>
    ),
  },

  {
    id: 'data-load',
    category: 'data',
    title: 'Αρχεία κατανάλωσης (φορτίου) κτιρίου',
    summary: 'Το ωριαίο αρχείο κατανάλωσης ηλεκτρικής ενέργειας — το πιο σημαντικό αρχείο για κάθε κτίριο.',
    body: (
      <>
        <p>
          Ένα <strong>Building Load Profile</strong> καταγράφει πόση ηλεκτρική ενέργεια χρησιμοποίησε ένα κτίριο,
          μία τιμή ανά ώρα. Αποτελεί τη βάση για την πρόβλεψη και τη βελτιστοποίηση, γι' αυτό ετοιμάστε
          ένα για κάθε κτίριο αν είναι δυνατό.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Στήλη</th><th>Απαιτείται;</th><th>Σημασία</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Απαιτείται</td><td>Η ώρα, π.χ. <Code>2025-01-01T08:00:00Z</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Απαιτείται</td><td>Ηλεκτρική ενέργεια που χρησιμοποιήθηκε εκείνη την ώρα, σε kWh (αριθμός, δεκαδική τελεία, ποτέ αρνητικός)</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Προαιρετικό</td><td>Μέγιστη απαίτηση ισχύος εκείνη την ώρα, σε kW</td></tr>
          </tbody>
        </Table>
        <p><strong>Παράδειγμα:</strong></p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,consumption_kwh
2025-01-01T00:00:00Z,0.457
2025-01-01T01:00:00Z,0.343
2025-01-01T02:00:00Z,0.298`}
        </pre>
        <p>Μία τιμή ανά ώρα· ένα πλήρες έτος αποτελείται από 8.760 γραμμές. Απαιτούνται τουλάχιστον 24 γραμμές (μία μέρα).</p>
        <TemplateBtn type="building_load_profile">Λήψη προτύπου φορτίου</TemplateBtn>
        <Warn>Η κατανάλωση δεν μπορεί ποτέ να είναι αρνητική. Μια αρνητική τιμή σημαίνει ότι μπερδεύτηκε μια στήλη — ελέγξτε ότι η παραγωγή δεν έχει τοποθετηθεί στη στήλη κατανάλωσης.</Warn>
      </>
    ),
  },

  {
    id: 'data-generation',
    category: 'data',
    title: 'Αρχεία ηλιακής &amp; αιολικής παραγωγής',
    summary: 'Ωριαία παραγωγή ηλιακών πάνελ και ανεμογεννητριών.',
    body: (
      <>
        <p>Τα αρχεία παραγωγής καταγράφουν πόση ενέργεια παρήγαγε μια ανανεώσιμη πηγή, μία τιμή ανά ώρα.</p>
        <h5 className="mt-3">Κοινοτικό ηλιακό πάρκο ή ανεμογεννήτρια</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Στήλη</th><th>Σημασία</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Η ώρα, π.χ. <Code>2025-01-01T12:00:00Z</Code></td></tr>
            <tr><td><Code>generation_kw</Code></td><td>Ισχύς που παρήχθη εκείνη την ώρα, σε kW (ποτέ αρνητική — τη νύχτα η ηλιακή είναι <Code>0</Code>)</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,generation_kw
2025-01-01T00:00:00Z,0
2025-01-01T12:00:00Z,105.6
2025-01-01T18:00:00Z,12.3`}
        </pre>
        <TemplateBtn type="pv_generation_profile">Πρότυπο ηλιακής (PV)</TemplateBtn>
        <TemplateBtn type="wind_generation_profile">Πρότυπο αιολικής</TemplateBtn>

        <h5 className="mt-4">Ηλιακά πάνελ οροφής μεμονωμένου κτιρίου</h5>
        <p>
          Για ηλιακή ενέργεια που ανήκει σε ένα μόνο κτίριο, η στήλη τιμής είναι
          <Code>generation_kwh</Code> (ενέργεια σε kWh) αντί για <Code>generation_kw</Code>.
          Η πλατφόρμα εμφανίζει το ακριβές όνομα στήλης που απαιτείται όταν επιλέγετε τον τύπο συνόλου δεδομένων στη
          σελίδα μεταφόρτωσης — ακολουθείτε πάντα αυτό που εμφανίζει.
        </p>
        <Tip>Οι ώρες χωρίς ήλιο ή άνεμο πρέπει να περιέχουν <Code>0</Code>, όχι να αφήνονται κενές.</Tip>
      </>
    ),
  },

  {
    id: 'data-tariff',
    category: 'data',
    title: 'Αρχεία τιμολογίου (τιμή ηλεκτρικής ενέργειας)',
    summary: 'Τιμές εισαγωγής και εξαγωγής ηλεκτρικής ενέργειας — ιστορικές και day-ahead.',
    body: (
      <>
        <p>Τα αρχεία τιμολογίου ενημερώνουν την πλατφόρμα για την τιμή της ηλεκτρικής ενέργειας για κάθε ώρα.</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Αρχείο</th><th>Στήλη τιμής</th><th>Γραμμές</th></tr>
          </thead>
          <tbody>
            <tr><td>Ιστορικό Τιμολόγιο Εισαγωγής</td><td><Code>import_price_eur_kwh</Code></td><td>Ένα έτος, ωριαίο (≈8.760)</td></tr>
            <tr><td>Ιστορικό Τιμολόγιο Εξαγωγής</td><td><Code>export_price_eur_kwh</Code></td><td>Ένα έτος, ωριαίο (≈8.760)</td></tr>
            <tr><td>Τιμολόγιο Εισαγωγής Day-Ahead</td><td><Code>import_price_eur_kwh</Code></td><td>24 (μία μέρα)</td></tr>
            <tr><td>Τιμολόγιο Εξαγωγής Day-Ahead</td><td><Code>export_price_eur_kwh</Code></td><td>24 (μία μέρα)</td></tr>
          </tbody>
        </Table>
        <p>
          Η τιμή <strong>εισαγωγής</strong> είναι αυτό που πληρώνετε για να αγοράσετε ηλεκτρική ενέργεια· η τιμή <strong>εξαγωγής</strong>
          είναι αυτό που εισπράττετε για να την πουλήσετε. Οι τιμές είναι σε ευρώ ανά kWh (π.χ. <Code>0.22</Code>)
          και χρησιμοποιούν δεκαδική τελεία.
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,import_price_eur_kwh
2025-01-01T00:00:00Z,0.08
2025-01-01T12:00:00Z,0.22
2025-01-01T19:00:00Z,0.28`}
        </pre>
        <TemplateBtn type="energy_tariff_data">Πρότυπο τιμολογίου εισαγωγής</TemplateBtn>
        <TemplateBtn type="feed_in_tariff_data">Πρότυπο τιμολογίου εξαγωγής</TemplateBtn>
        <Tip>
          Τα αρχεία <strong>Day-ahead</strong> καλύπτουν μόνο μία μέρα (24 γραμμές). Μεταφορτώστε ένα νέο κάθε μέρα —
          οι χρονικές σημάνσεις του πρέπει να αντιστοιχούν στην ημερομηνία εκείνης της μέρας. Τα παραπάνω πρότυπα λειτουργούν επίσης ως
          αναφορά μορφής 24 γραμμών.
        </Tip>

        <h5 className="mt-4">Πού μπορώ να βρω δεδομένα τιμολογίου;</h5>
        <p>
          Δύο διαφορετικά πράγματα ονομάζονται «τιμολόγιο» — χειριστείτε τα ξεχωριστά:
        </p>
        <ul>
          <li><strong>Τιμές χονδρικής / day-ahead</strong> — η ωριαία τιμή αγοράς που δημοσιεύεται από
            το χρηματιστήριο ενέργειας της χώρας σας. Χρησιμοποιήστε αυτό για αρχεία τιμολογίου <em>day-ahead</em> και ως
            καλή προσέγγιση μεταβλητών συμβολαίων.</li>
          <li><strong>Τιμολόγιο λιανικής</strong> — αυτό που πληρώνετε πραγματικά (ενέργεια + χρεώσεις δικτύου + φόροι).
            Λάβετε το από το συμβόλαιο ή τον λογαριασμό του παρόχου σας.</li>
        </ul>

        <p className="mt-3"><strong>Πανευρωπαϊκή πηγή (λειτουργεί για όλες τις χώρες):</strong></p>
        <ul>
          <li><strong>ENTSO-E Transparency Platform</strong> —
            {' '}<Ext href="https://transparency.entsoe.eu">transparency.entsoe.eu</Ext>. Δωρεάν, αλλά
            απαιτεί δωρεάν λογαριασμό. Ανοίξτε <em>Market → Day-ahead Prices</em>, επιλέξτε τη ζώνη προσφοράς σας
            (π.χ. IT-North, FR, DE-LU, CZ, GR), το εύρος ημερομηνιών και <em>Export Data</em> → CSV.
            Η στήλη τιμής είναι σε <strong>€/MWh — διαιρέστε με 1000 για μετατροπή σε €/kWh</strong>
            (π.χ. 220 €/MWh = 0,22 €/kWh).</li>
        </ul>

        <p className="mt-3"><strong>Εθνικές πηγές (συχνά απλούστερες, χωρίς λογαριασμό):</strong></p>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Χώρα</th><th>Ιστότοπος</th><th>Τι λαμβάνετε</th></tr>
          </thead>
          <tbody>
            <tr><td>Ιταλία</td><td><Ext href="https://www.mercatoelettrico.org">mercatoelettrico.org</Ext> (GME)</td><td>Τιμή day-ahead PUN, ωριαία. Αναζητήστε «Esiti MGP». €/MWh — διαιρέστε με 1000.</td></tr>
            <tr><td>Γαλλία</td><td><Ext href="https://data.rte-france.com">data.rte-france.com</Ext> (RTE Open Data) ή <Ext href="https://www.epexspot.com/en">epexspot.com</Ext></td><td>RTE: δωρεάν ανοιχτή πύλη δεδομένων, ωριαίες τιμές day-ahead. EPEX: ιστότοπος αγοράς.</td></tr>
            <tr><td>Γερμανία</td><td><Ext href="https://www.smard.de/en">smard.de</Ext> (Ομοσπονδιακή Ρυθμιστική Αρχή)</td><td>«Market data» → «Wholesale prices» → «Day-ahead, Germany/Luxembourg». Δωρεάν λήψη, χωρίς λογαριασμό.</td></tr>
            <tr><td>Τσεχία</td><td><Ext href="https://www.ote-cr.cz/en">ote-cr.cz</Ext> (OTE)</td><td>«Short-term markets» → «Day-ahead market». Εξαγωγή CSV ανά ημέρα.</td></tr>
            <tr><td>Ελλάδα</td><td><Ext href="https://www.enexgroup.gr/web/guest/markets-publications-el-day-ahead-market">enexgroup.gr</Ext> (HEnEx)</td><td>Αποτελέσματα Αγοράς Day-Ahead, ημερήσια PDF και CSV. €/MWh — διαιρέστε με 1000.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Μετατροπή ληφθέντος αρχείου τιμών στη μορφή της πλατφόρμας</h5>
        <p>
          Οι εξαγωγές από χρηματιστήρια ενέργειας σπάνια έχουν ακριβώς τα ονόματα στηλών που θέλει η πλατφόρμα. Χρησιμοποιήστε τα
          παρακάτω βήματα για να μετατρέψετε οποιοδήποτε από τα παραπάνω αρχεία:
        </p>
        <ol>
          <li>Ανοίξτε το ληφθέν CSV στο Excel ή στο Google Sheets.</li>
          <li>Εντοπίστε τη στήλη ημερομηνίας/ώρας και τη στήλη τιμής (οι άλλες στήλες μπορούν να διαγραφούν).</li>
          <li>Δημιουργήστε μια στήλη με το όνομα <Code>timestamp</Code>. Αν η πηγή έχει ήδη χρονική σήμανση ISO,
            αντιγράψτε την· διαφορετικά δημιουργήστε μία με τον τύπο από το
            {' '}<ArticleLink to="data-csv-basics">Δημιουργία αρχείων CSV σωστά</ArticleLink>.</li>
          <li>Δημιουργήστε μια στήλη με το όνομα <Code>import_price_eur_kwh</Code>. Αν η πηγή είναι σε €/MWh, διαιρέστε
            με 1000 με έναν τύπο όπως <Code>=B2/1000</Code> και επικολλήστε ως τιμές.</li>
          <li>Αποθηκεύστε ως <strong>CSV UTF-8</strong> και μεταφορτώστε ως <em>Ιστορικό Τιμολόγιο Εισαγωγής</em>.</li>
        </ol>

        <Warn>
          Τιμή χονδρικής ≠ τιμή λιανικής. Η τιμή χονδρικής είναι μόνο η τιμή της αγοράς ενέργειας και
          δεν περιλαμβάνει χρεώσεις δικτύου, εισφορές ή ΦΠΑ. Για ακριβή ανάλυση κόστους, προτιμήστε ένα τιμολόγιο λιανικής
          από τον πάροχό σας ή συνδυάστε την τιμή χονδρικής με γνωστό σταθερό πρόσθετο.
        </Warn>

        <h5 className="mt-4">Τιμολόγιο εξαγωγής (feed-in)</h5>
        <p>
          Οι τιμές εξαγωγής εξαρτώνται από το εθνικό σας σχήμα — μπορεί να είναι σταθερό τιμολόγιο feed-in, η
          τιμή χονδρικής μείον έκπτωση, ή μηδέν. Λάβετε τον κανόνα από τον πάροχό σας, τον DSO σας ή
          τον εθνικό ρυθμιστή (ARERA στην Ιταλία, CRE στη Γαλλία, BNetzA στη Γερμανία, ERÚ στην Τσεχία,
          RAE στην Ελλάδα) και γράψτε την προκύπτουσα τιμή σε €/kWh για κάθε ώρα.
        </p>
      </>
    ),
  },
]
